"use client";

import { useState, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Thumbs } from "swiper/modules";
import { isWhatuni } from "@packages/lib/utlils/commonFunction";
import Image from "next/image";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/thumbs";
import Video from "../videos/Video";
import { GA4DataLayer } from "@packages/lib/utlils/helper-function";
import { GAConstants } from "@packages/shared-components/services/constants";

interface galleryProps {
  mediaId: number;
  mediaPath: string;
  thumbnailImage: string;
  mediaType?: string;
  userReview: boolean;
  videoFlag?: string;
  commonGAAttrs?: any;
  showGallery?: any;
}

const ReviewThumbGallerySliderComponents = ({
  galleryItems,
  isPostgraduate,
  noVideoFlag,
  userReview = false,
  isOpen = true,
  commonGAAttrs,
  showGallery,
  isFullWidth = false,
  isOpenDayPage = false,
}: {
  galleryItems: galleryProps[];
  isPostgraduate?: boolean;
  noVideoFlag?: boolean;
  userReview?: boolean;
  isOpen?: boolean;
  commonGAAttrs?: any;
  showGallery?: any;
  isFullWidth?: boolean;
  isOpenDayPage?: boolean;
}) => {
  const [thumbsSwiper, setThumbsSwiper] = useState<any>(null);
  const swiperWrapperRef = useRef<HTMLDivElement | null>(null);

  if (!galleryItems?.length) return <></>;

  // Filter out items with missing media paths to avoid undefined in image URLs
  const validGalleryItems = galleryItems?.filter(
    (item) => item?.mediaPath || item?.thumbnailImage,
  );

  if (!validGalleryItems?.length) return <></>;

  const handleSlideChange = (swiper: any) => {
    if (swiperWrapperRef.current) {
      const videos = swiperWrapperRef.current.querySelectorAll("video");
      videos?.forEach((video) => {
        if (!video.paused) {
          video.pause();
        }
      });

      // const currentSlide = swiper.slides[swiper.activeIndex];
      // const currentVideo = currentSlide.querySelector('video');
      // if (currentVideo) {
      //   currentVideo.play();
      // }
    }
  };
  const handleSlide = () => {
    GA4DataLayer({
      event: GAConstants.track_event,
      event_name: "carousel_navigation",
      click_text: showGallery
        ? "Show Gallery"
        : "Is " + commonGAAttrs?.institution_name + " a good place to study?",
      // field:"arrow"
      navigation_category: "arrow",
      ...commonGAAttrs,
    });
  };

  function getFullImageUrl(path: string): string {
    // If the path is already an absolute URL, don't prepend the image domain
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    return `${process.env.NEXT_PUBLIC_IMAGE_DOMAIN}${path}`;
  }

  function addSizeToImagePath(path: string, size: string): string {
    // Skip size suffix for user review images as sized variants don't exist
    if (userReview) return path;
    // Check if the path ends with .jpg
    if (!path.endsWith(".jpg")) return path;
    // Insert _{size} before the .jpg extension
    return path.replace(/\.jpg$/, `_${size}.jpg`);
  }

  return (
    <>
      <div
        ref={swiperWrapperRef}
        className={`w-full mx-auto ${isFullWidth ? "" : "max-w-lg md:w-[728px] lg:w-[670px] xl:w-[907px]"} ${userReview ? "md:!w-full" : ""}`}
      >
        {isOpen && (
          <>
            {/* Main Swiper */}
            <Swiper
              onSlideChange={handleSlideChange}
              onSlideNextTransitionStart={handleSlide}
              onSlidePrevTransitionStart={handleSlide}
              pagination={true}
              spaceBetween={8}
              navigation={true}
              // autoHeight={true}
              thumbs={{ swiper: thumbsSwiper }}
              modules={[Navigation, Pagination, Thumbs]}
              className="reviewMainSwiper MultiSwiper"
            >
              {validGalleryItems.length > 0 &&
                validGalleryItems?.map((item: any, index: number) => (
                  <SwiperSlide key={index}>
                    <div
                      className={`w-full h-[185px] rounded-[8px] overflow-hidden flex items-center justify-center sm:h-[360px] md:h-[390px] ${userReview ? "lg:h-[390px] xl:h-[410px]" : isOpenDayPage ? "md:h-[411px] md:max-w-[728px] lg:h-[500px] xl:h-[686px] xl:max-w-[1216px]" : "lg:h-[450px] xl:h-[480px]"} `}
                    >
                      {item.videoFlag !== "Y" || noVideoFlag ? (
                        item?.mediaPath ? (
                          <Image
                            src={addSizeToImagePath(
                              getFullImageUrl(item?.mediaPath),
                              "768px",
                            )}
                            alt={`Slide ${index}`}
                            className="w-full h-full object-cover rounded-[8px]"
                            width="907"
                            height="511"
                          />
                        ) : null
                      ) : (
                        <Video
                          commonGAAttrs={{
                            ...commonGAAttrs,
                            position: index + 1,
                          }}
                          featuredData={{
                            mediaPath: item?.mediaPath,
                            mediaName: item?.mediaName,
                            thumbnailUrl: item?.thumbnailImage
                              ? getFullImageUrl(item?.thumbnailImage)
                              : "",
                          }}
                        />
                      )}
                    </div>
                  </SwiperSlide>
                ))}
            </Swiper>

            {/* Thumbnail Swiper */}
            <Swiper
              onSwiper={setThumbsSwiper}
              slidesPerView="auto"
              spaceBetween={8}
              slidesPerGroupSkip={3}
              freeMode={true}
              watchSlidesProgress={true}
              breakpoints={{
                320: {
                  // slidesPerView: 1,
                  spaceBetween: 8,
                  slidesPerGroup: 3,
                },
                768: {
                  // slidesPerView: 5,
                  spaceBetween: 10,
                  slidesPerGroup: 3,
                },
                993: {
                  // slidesPerView: 5,
                  spaceBetween: 4,
                  slidesPerGroup: 3,
                },
              }}
              modules={[Thumbs]}
              // className={`${!isWhatuni() || isPostgraduate ? "pgsthumswiper" : "thumbSwiper"}`}
              // className="thumbSwiper"
              className={`${!isWhatuni() || isPostgraduate ? "pgsthumswiper" : "thumbSwiper"} ${userReview ? "reviewsSwiper" : ""}`}
            >
              {validGalleryItems?.length > 0 &&
                validGalleryItems.map((item: any, index: number) => (
                  <SwiperSlide key={index} className="cursor-pointer">
                    {item.videoFlag !== "Y" ? (
                      item?.mediaPath ? (
                        <Image
                          width={157}
                          height={112}
                          src={addSizeToImagePath(
                            getFullImageUrl(item.mediaPath),
                            "170px",
                          )}
                          alt={`Thumbnail ${index}`}
                          className="w-full h-full object-cover rounded-[4px]"
                        />
                      ) : null
                    ) : (
                      <>
                        <div className="rounded-[4px] h-full">
                          <button className="absolute left-[50%] top-[50%] -translate-x-[50%] -translate-y-[50%] cursor-pointer">
                            <Image
                              alt="video_play_icon"
                              loading="lazy"
                              width="52"
                              height="52"
                              decoding="async"
                              data-nimg="1"
                              src="/static/assets/icons/video_play_icon.svg"
                            />
                          </button>
                          {item?.thumbnailImage && (
                            <Image
                              width={157}
                              height={112}
                              src={
                                isWhatuni() && !isPostgraduate
                                  ? addSizeToImagePath(
                                      getFullImageUrl(item.thumbnailImage),
                                      "170px",
                                    )
                                  : getFullImageUrl(item.thumbnailImage)
                              }
                              alt={`Thumbnail ${index}`}
                              className="w-full h-full object-cover rounded-[4px]"
                            />
                          )}
                        </div>
                      </>
                    )}
                  </SwiperSlide>
                ))}
            </Swiper>
          </>
        )}
      </div>
    </>
  );
};

export default ReviewThumbGallerySliderComponents;
